// DN detail «Түүх» tab: custody transfers (хүлээлцэл) tied to this DN (GET /ord/custody/transfers?deliveryNoteId=).
// Read-only; each row opens the handover detail where initiate/verify live (O95: moved off the dead detail-form).

import { Anchor, Box, Group, Loader, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import useSWR from "swr"
import { CustodyTransferApi } from "../../apis"
import type { ICustodyTransfer } from "../../apis/custody-transfer"
import { dateTimeSecFormat } from "../../utils/date"
import { CustodyStatusBadge, TransferTypeBadge } from "../custody-transfers/atoms"
import classes from "./dn-detail.module.css"

export function HandoverLogPanel({ deliveryNoteId }: Readonly<{ deliveryNoteId: string }>) {
  const { t } = useTranslation("delivery")

  const { data, isLoading } = useSWR(
    deliveryNoteId ? `/ord/custody/transfers?dn=${deliveryNoteId}` : null,
    () => CustodyTransferApi.listByDn(deliveryNoteId),
    { revalidateOnFocus: false },
  )
  const rows: ICustodyTransfer[] = data?.rows ?? []

  return (
    <Box className={classes.attachRow}>
      <Text className={classes.sectionTitle} mb="md">
        {t("delivery.handover.title")}
      </Text>
      {isLoading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : rows.length === 0 ? (
        <Text className={classes.hint} ta="center" py="md">
          {t("delivery.handover.empty")}
        </Text>
      ) : (
        <Stack gap={0}>
          {rows.map((row) => {
            const stamps = [
              row.initiatedAt && `${t("delivery.handover.initiated")}: ${dateTimeSecFormat(row.initiatedAt)}`,
              row.senderVerifiedAt && `${t("delivery.handover.senderVerified")}: ${dateTimeSecFormat(row.senderVerifiedAt)}`,
              row.receiverVerifiedAt && `${t("delivery.handover.receiverVerified")}: ${dateTimeSecFormat(row.receiverVerifiedAt)}`,
            ].filter(Boolean)
            const cancelNote =
              row.cancelledReasonCode === "DUPLICATE_SUPERSEDED" ? t("manage.handover.duplicateCancelled") : row.cancelledReason
            return (
              <Group key={row.id} className={classes.stopRow} justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} miw={0}>
                  <Group gap="sm" wrap="wrap">
                    <TransferTypeBadge type={row.transferType} />
                    <CustodyStatusBadge status={row.status} />
                  </Group>
                  {stamps.length > 0 && <Text className={classes.label}>{stamps.join(" · ")}</Text>}
                  {row.status === "CANCELLED" && cancelNote && (
                    <Text className={classes.label}>
                      {t("delivery.handover.cancelledReason")}: {cancelNote}
                    </Text>
                  )}
                </Stack>
                <Stack gap={4} align="flex-end">
                  <Anchor component={Link} to={`/handover/${row.id}`} className={classes.link}>
                    {t("delivery.handover.open")}
                  </Anchor>
                  <Text className={classes.label}>
                    {t("delivery.handover.itemCount", { count: row.expectedItems?.length ?? 0 })}
                  </Text>
                </Stack>
              </Group>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}
